import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { downloadReportPdf, fetchReportJson, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import { formatReportTime } from '../../utils/dailyTransactionFuelSalesRows';
import './other-reports/report-table.css';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportFilterCard,
  OtherReportFilterSection,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportSelectField,
  OtherReportShell,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

const formatQuantity = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '';
  }

  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(num);
};

const formatShiftTime = (row) => {
  if (row.shift_time) {
    return row.shift_time;
  }

  const start = formatReportTime(row.start_from);
  const end = formatReportTime(row.end_from);
  if (start && end) {
    return `${start} - ${end}`;
  }

  return start || end || '';
};

const formatOpeningReading = (row) => {
  if (row.opening_reading === null || row.opening_reading === undefined || row.opening_reading === '') {
    return '';
  }

  return formatQuantity(row.opening_reading);
};

const formatCloseReading = (row) => {
  if (row.is_closed === false || row.close_reading === 'not_closed' || row.close_reading === null || row.close_reading === '') {
    return 'not_closed';
  }

  return formatQuantity(row.close_reading);
};

const csvEscape = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
};

const NozzleReport = (props) => {
  const [meta, setMeta] = useState({ employees: [], nozzles: [] });
  const [inputValues, setInputValues] = useState({ nozzles: [], employee_id: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/nozzle_report/meta`)
      .then(setMeta)
      .catch((error) => swal('Nozzle Report', error.message, 'error'));
  }, []);

  const employeeOptions = useMemo(
    () => [
      { value: '', label: 'All' },
      ...(meta.employees || []).map((employee) => ({
        value: String(employee.id),
        label: employee.name,
      })),
    ],
    [meta.employees],
  );

  const reportRows = report?.rows?.length
    ? report.rows
    : (report?.employee_groups || []).flatMap((group) => group.rows || []);

  const toggleNozzle = (id) => {
    const selected = new Set((inputValues.nozzles || []).map(String));
    const key = String(id);
    if (selected.has(key)) {
      selected.delete(key);
    } else {
      selected.add(key);
    }
    setInputValues({ ...inputValues, nozzles: Array.from(selected) });
  };

  const setNozzles = (ids) => {
    setInputValues({ ...inputValues, nozzles: (ids || []).map(String) });
  };

  const buildPayload = () => {
    const payload = {
      from_date: inputValues.start_date,
      to_date: inputValues.end_date,
      nozzles: inputValues.nozzles || [],
    };
    if (inputValues.employee_id) {
      payload.employee_id = inputValues.employee_id;
    }

    return payload;
  };

  const validateFilters = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }

    return true;
  };

  const loadReport = async () => {
    if (!validateFilters()) {
      return;
    }

    setLoading(true);
    setReport(null);
    try {
      setReport(await fetchReportJson(`${API_URL}/nozzle_report`, buildPayload()));
    } catch (error) {
      swal('Nozzle Report', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!validateFilters()) {
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/nozzle_report/pdf`,
        buildPayload(),
        'nozzle_report.pdf',
      );
    } catch (error) {
      swal('Nozzle Report', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadExcel = () => {
    if (!report || reportRows.length === 0) {
      swal('Nozzle Report', 'Run the report first, then download Excel.', 'warning');
      return;
    }

    const headers = [
      'Date',
      'Shift Time',
      'Employee Name',
      'Nozzle',
      'Tank Name',
      'Opening Reading',
      'Close Reading',
      'Quantity',
    ];
    const lines = [headers.map(csvEscape).join(',')];
    reportRows.forEach((row) => {
      lines.push([
        formatReportDate(row.op_date),
        formatShiftTime(row),
        row.emp_name || '',
        row.nozzle_name || '',
        row.tank_name || '',
        formatOpeningReading(row),
        formatCloseReading(row),
        row.is_closed === false ? '' : formatQuantity(row.quantity),
      ].map(csvEscape).join(','));
    });
    lines.push([
      'Overall',
      '',
      '',
      '',
      '',
      '',
      report.overall?.open_count ? `${report.overall.open_count} not_closed` : '',
      formatQuantity(report.overall?.quantity),
    ].map(csvEscape).join(','));

    const blob = new Blob([`\uFEFF${lines.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'nozzle_report.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(link.href);
  };

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Nozzle Report" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                    <OtherReportSelectField
                      label="Employee Name"
                      value={inputValues.employee_id ?? ''}
                      placeholder="All employees"
                      span={3}
                      options={employeeOptions}
                      isClearable={false}
                      onChange={(value) => setInputValues({ ...inputValues, employee_id: value })}
                    />
                  </>
                )}
                actions={(
                  <div className="other-report-actions">
                    <OtherReportActionButtons
                      loading={loading}
                      pdfLoading={pdfLoading}
                      onSubmit={loadReport}
                      onDownloadPdf={downloadPdf}
                    />
                    <button
                      type="button"
                      className="other-report-btn other-report-btn-secondary"
                      onClick={downloadExcel}
                      disabled={loading || pdfLoading || !report}
                    >
                      Download Excel
                    </button>
                  </div>
                )}
              />
              <OtherReportFilterSection
                title="Nozzle Name"
                description="Optional — leave empty to include all nozzles."
              >
                <OtherReportSearchableCheckList
                  items={meta.nozzles || []}
                  selectedIds={inputValues.nozzles || []}
                  onToggle={toggleNozzle}
                  onChange={setNozzles}
                  searchPlaceholder="Search nozzles…"
                  getId={(item) => String(item.id)}
                  getLabel={(item) => item.nozzle_name}
                />
              </OtherReportFilterSection>
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating nozzle report" />}
              {report && !loading && (
                <div className="form-wrap balace-sheetwrap">
                  <div className="row mb-3">
                    <div className="col-md">
                      <h2 className="table-title mb-1">{report.pump_name}</h2>
                      <h4 className="table-title mb-1">Nozzle Report</h4>
                      <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                      <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                    </div>
                  </div>

                  <table className="table border">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Shift Time</th>
                        <th>Employee Name</th>
                        <th>Nozzle</th>
                        <th>Tank Name</th>
                        <th className="report-amount-header">Opening Reading</th>
                        <th className="report-amount-header">Close Reading</th>
                        <th className="report-amount-header">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-muted">No nozzle allocations in this period.</td>
                        </tr>
                      ) : reportRows.map((row, rowIndex) => {
                        const closed = row.is_closed !== false && row.close_reading !== 'not_closed';
                        return (
                          <tr
                            key={`${row.allocation_id || rowIndex}-${row.nozzle_id || rowIndex}`}
                            className={closed ? '' : 'table-warning'}
                          >
                            <td>{formatReportDate(row.op_date)}</td>
                            <td>{formatShiftTime(row)}</td>
                            <td>{row.emp_name}</td>
                            <td>{row.nozzle_name}</td>
                            <td>{row.tank_name}</td>
                            <td className="report-amount">{formatOpeningReading(row)}</td>
                            <td className="report-amount">{formatCloseReading(row)}</td>
                            <td className="report-amount">{closed ? formatQuantity(row.quantity) : ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {report.overall && (
                    <div className="alert alert-info">
                      Overall quantity: {formatQuantity(report.overall.quantity)}
                      {(report.overall.open_count || 0) > 0
                        ? ` · ${report.overall.open_count} nozzle(s) not_closed`
                        : ''}
                    </div>
                  )}
                </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default NozzleReport;
